import { makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { Parameter } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

// Monotonic per-instance key for React list rendering. Parameters have no stable server id, so each instance gets a unique
// key at construction that never changes across add/remove/reorder.
let nextParameterKey = 0;

/**
 * One node parameter, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * All fields are plain public observables. `parameterType` and `inout` are always-set enums (a dropdown never clears them);
 * the rest are nullable free text. `options` is the comma-separated string the dropdown/key-value types edit (opaque JSON on
 * the wire, but the UI has always treated it as a string). `order` is not stored — array position in the aggregate conveys
 * it, and `toSave` stamps it from the index.
 */
export class EditableParameter {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableParameter>;
  defaultExpression: string | null = null;
  description: string | null = null;
  inout: string = 'inout';
  /** Stable identity for React list keys / drag ids; never changes after construction. */
  readonly key = `parameter-${(nextParameterKey += 1)}`;
  mandatory = false;
  name: string | null = null;
  options: string | null = null;
  outputFormat: string | null = null;
  parameterType: string = 'String';
  runtime = false;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds from a loaded parameter; when absent, a fresh String parameter.
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableParameter>, wire?: Parameter) {
    this._aggregate = aggregate;
    makeObservable(this, {
      defaultExpression: observable,
      description: observable,
      inout: observable,
      mandatory: observable,
      name: observable,
      options: observable,
      outputFormat: observable,
      parameterType: observable,
      runtime: observable,
    });
    if (wire !== undefined) {
      this.name = wire.name;
      this.parameterType = wire.parameter_type || 'String';
      this.defaultExpression = wire.default_expression ?? null;
      this.inout = wire.inout || 'inout';
      // Opaque JSON on the wire; the UI edits it as a comma-separated string, so coerce whatever came back to a string.
      this.options = wire.options == null ? null : String(wire.options);
      this.outputFormat = wire.output_format ?? null;
      this.description = wire.description ?? null;
      this.runtime = wire.runtime ?? false;
      this.mandatory = wire.mandatory ?? false;
    }
  }

  /** A one-line summary for the card subtitle. */
  get summary(): string {
    return `${this.name || '(unnamed)'} · ${this.parameterType}`;
  }

  /** Remove this parameter from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. `order` is stamped from the caller's array index (1-based) — array position is the source of
   * truth for order, so the caller passes it rather than the parameter storing its own.
   */
  toSave(order: number): Parameter {
    return {
      name: this.name ?? '',
      parameter_type: this.parameterType,
      default_expression: this.defaultExpression,
      runtime: this.runtime,
      inout: this.inout,
      options: this.options,
      mandatory: this.mandatory,
      output_format: this.outputFormat,
      order,
      description: this.description,
    };
  }
}
